// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

// Faithful re-implementation of @medplum/react@5.1.7 ThreadChat on top of
// BaseChat, adding client-side tenant scoping. If @medplum/react is upgraded,
// re-diff ThreadChat/BaseChat against this file.

import { usePrevious } from '@mantine/hooks';
import { createReference, formatCodeableConcept, getReferenceString } from '@medplum/core';
import type { Communication, CommunicationPayload, DocumentReference, Reference } from '@medplum/fhirtypes';
import { BaseChat, useMedplum, useMedplumProfile } from '@medplum/react';
import { getTenantAccountRef, withTenantScope } from '@polarissleep/shared';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';

export interface TenantScopedThreadChatProps {
  readonly thread: Communication;
  readonly organizationId: string | undefined;
  readonly title?: string;
  readonly onMessageSent?: (message: Communication) => void;
  readonly inputDisabled?: boolean;
  readonly excludeHeader?: boolean;
  readonly uploadEnabled?: boolean;
  readonly onError?: (err: Error) => void;
  readonly onViewInDocuments?: (reference: Reference<DocumentReference>) => void;
}

export function TenantScopedThreadChat(props: TenantScopedThreadChatProps): JSX.Element | null {
  const {
    thread,
    organizationId,
    title,
    onMessageSent,
    inputDisabled,
    excludeHeader,
    uploadEnabled,
    onError,
    onViewInDocuments,
  } = props;

  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const [communications, setCommunications] = useState<Communication[]>([]);

  const profileRef = useMemo(() => (profile ? createReference(profile) : undefined), [profile]);
  const threadRef = useMemo(() => createReference(thread), [thread]);

  const previousThreadId = usePrevious(thread?.id);
  useEffect(() => {
    if (previousThreadId !== thread?.id) {
      setCommunications([]);
    }
  }, [previousThreadId, thread?.id]);

  const sendMessage = useCallback(
    (message: string, file?: File, existingDocRef?: DocumentReference): void => {
      const buildAndSend = async (): Promise<void> => {
        const profileRefStr = profileRef ? getReferenceString(profileRef) : undefined;
        if (!profileRefStr) {
          return;
        }

        const payload: CommunicationPayload[] = [];

        if (message) {
          payload.push({ contentString: message });
        }

        if (existingDocRef) {
          payload.push({ contentReference: createReference(existingDocRef) });
        } else if (file) {
          const accountRef = getTenantAccountRef(organizationId);
          const docRef = await medplum.createDocumentReference({
            data: file,
            contentType: file.type || 'application/octet-stream',
            filename: file.name,
            additionalFields: {
              ...(thread.subject ? { subject: thread.subject } : {}),
              description: file.name,
              ...(accountRef ? { meta: { account: accountRef } } : {}),
            },
          });
          payload.push({ contentReference: createReference(docRef) });
        }

        const communication = await medplum.createResource<Communication>(
          withTenantScope(
            {
              resourceType: 'Communication',
              status: 'in-progress',
              sender: profileRef,
              recipient: thread.recipient?.filter((ref) => getReferenceString(ref) !== profileRefStr) ?? [],
              sent: new Date().toISOString(),
              payload,
              partOf: [threadRef],
              subject: thread.subject,
            },
            organizationId
          )
        );

        // Intentional improvement over upstream's `[...communications, communication]`:
        // functional form avoids a stale-closure bug; `communications` is deliberately
        // omitted from the deps array so BaseChat's setCommunications prop stays stable.
        setCommunications((prev) => [...prev, communication]);
        onMessageSent?.(communication);
      };

      buildAndSend().catch(console.error);
    },
    [medplum, profileRef, thread, threadRef, onMessageSent, organizationId]
  );

  const onMessageReceived = useMemo(() => {
    if (thread.recipient?.length !== 2) {
      return undefined;
    }
    return (receivedMessage: Communication): void => {
      // Restore upstream compound guard: skip only when already received AND completed.
      // A completed-but-unreceived message still needs its `received` timestamp backfilled.
      if (receivedMessage.received && receivedMessage.status === 'completed') {
        return;
      }
      medplum
        .updateResource<Communication>({
          ...receivedMessage,
          received: receivedMessage.received ?? new Date().toISOString(),
          status: 'completed',
        })
        .catch(console.error);
    };
  }, [medplum, thread.recipient?.length]);

  if (!profile) {
    return null;
  }

  return (
    <BaseChat
      title={title ?? (thread?.topic ? formatCodeableConcept(thread.topic) : '[No thread title]')}
      communications={communications}
      setCommunications={setCommunications}
      query={`part-of=Communication/${thread.id as string}`}
      sendMessage={sendMessage}
      onMessageReceived={onMessageReceived}
      inputDisabled={inputDisabled}
      excludeHeader={excludeHeader}
      uploadEnabled={uploadEnabled}
      onError={onError}
      attachmentSubjectRef={thread.subject}
      onViewInDocuments={onViewInDocuments}
    />
  );
}
